import type { PresetActionProperties } from '#~/analytics/types/events.js';
import type { Device, Model } from '@studio/models/device';
import {
  resolveDeviceAnalyticsContext,
  resolveDeviceAnalyticsContextFromDevice
} from '#~/analytics/device-context.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { track } from '#~/analytics/track.js';
import { depot } from '#~/depot/index.js';
import { lookupRegistryTemplate } from '#~/device/registry/registry-config-cache.js';
import { logger } from '#~/logger/index.js';
import { generateId } from '@studio/models';

// The closure belongs to one logical action, not a render or a focused device.
export const presetActionReceipt = (deviceId: Device['id'], action: 'save' | 'recall') => {
  const context =
    resolveDeviceAnalyticsContext(deviceId) ??
    resolveDeviceAnalyticsContextFromDevice({ id: deviceId })!;
  const state = depot.getState();
  const model = context.device_model_id
    ? lookupRegistryTemplate(state.registry.templates, context.device_model_id as Model['id'])
        ?.model
    : undefined;
  const orgId = context.studio_id
    ? state.roomRecords[context.studio_id as never]?.organizationId
    : undefined;
  const properties: PresetActionProperties = {
    ...context,
    action,
    action_id: generateId(),
    driver_kind:
      model?.generic === true
        ? ('generic' as const)
        : model?.generic === false
          ? ('catalog' as const)
          : ('unknown' as const),
    // Both spellings: org_id is the canonical event-map property, orgId is what
    // the shared groups resolver already reads. Neither changes how unrelated
    // events resolve their organization.
    ...(orgId ? { org_id: orgId, orgId } : {})
  };
  let emitted = false;
  return () => {
    if (emitted) return;
    emitted = true;
    // Telemetry never turns a committed camera action into a failed user action.
    void track({
      category: EVENT_CATEGORIES.MOVEMENT_TOOLS,
      name:
        action === 'save' ? EVENT_NAMES.ADD_POSITION_PRESET : EVENT_NAMES.RECALL_POSITION_PRESET,
      properties
    }).catch((error: unknown) => logger.error('Preset analytics failed:', error));
  };
};
